import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Leaves({ user, onLogout }) {
  const [leaves, setLeaves] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [formData, setFormData] = useState({
    leave_type: 'Sick Leave',
    from_date: '',
    to_date: '',
    number_of_days: 1,
    reason: ''
  });

  useEffect(() => {
    fetchLeaves();
    fetchDashboardStats();
  }, []);

  const fetchLeaves = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/leaves`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaves(response.data);
    } catch (error) {
      console.error('Failed to fetch leaves:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/leaves`, {
        ...formData,
        start_date: formData.from_date,
        end_date: formData.to_date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Leave request submitted successfully!');
      setShowForm(false);
      setFormData({
        leave_type: 'Sick Leave',
        from_date: '',
        to_date: '',
        number_of_days: 1,
        reason: ''
      });
      fetchLeaves();
    } catch (error) {
      alert('Failed to submit leave request: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleApprove = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/leaves/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Leave approved successfully!');
      fetchLeaves();
    } catch (error) {
      alert('Failed to approve leave: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleReject = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/leaves/${id}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Leave rejected successfully!');
      fetchLeaves();
    } catch (error) {
      alert('Failed to reject leave: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Leave Management</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-6 py-3 rounded-lg text-white font-medium"
            style={{backgroundColor: '#2b3380'}}
          >
            {showForm ? 'Cancel' : '+ Apply Leave'}
          </button>
        </div>

        {/* Leave Management Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Leave Management Overview"
            icon="📝"
            bgColor="#ff9800"
            stats={[
              {
                label: 'Pending Leaves',
                value: dashboardStats.pending_leaves || 0,
                subtitle: 'Awaiting approval',
                color: '#ff9800'
              },
              {
                label: 'On Leave Today',
                value: dashboardStats.on_leave_today || 0,
                subtitle: 'Approved leaves',
                color: '#4caf50'
              },
              {
                label: 'Total Employees',
                value: dashboardStats.total_employees || 0,
                subtitle: 'Active staff',
                color: '#2b3380'
              },
              {
                label: 'Leave Balance',
                value: '9.0',
                subtitle: 'Default balance',
                color: '#607d8b'
              }
            ]}
          />
        )}

        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Apply for Leave</h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Leave Type</label>
                  <select
                    name="leave_type"
                    value={formData.leave_type}
                    onChange={(e) => setFormData({...formData, leave_type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="Sick Leave">Sick Leave</option>
                    <option value="Casual Leave">Casual Leave</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Number of Days</label>
                  <input
                    type="number"
                    name="number_of_days"
                    value={formData.number_of_days}
                    onChange={(e) => setFormData({...formData, number_of_days: parseFloat(e.target.value)})}
                    min="0.5"
                    step="0.5"
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">From Date</label>
                  <input
                    type="date"
                    name="from_date"
                    value={formData.from_date}
                    onChange={(e) => setFormData({...formData, from_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">To Date</label>
                  <input
                    type="date"
                    name="to_date"
                    value={formData.to_date}
                    onChange={(e) => setFormData({...formData, to_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Reason</label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                  required
                />
              </div>
              <button
                type="submit"
                className="px-6 py-3 rounded-lg text-white font-medium"
                style={{backgroundColor: '#2b3380'}}
              >
                Submit Request
              </button>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Leave Requests</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  {(user.role === 'Admin' || user.role === 'HR Manager') && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leaves.map((leave) => (
                  <tr key={leave.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{leave.employee_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{leave.leave_type}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{formatDate(leave.start_date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{formatDate(leave.end_date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{leave.number_of_days}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        leave.status === 'Approved' ? 'bg-green-100 text-green-800' :
                        leave.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {leave.status}
                      </span>
                    </td>
                    {(user.role === 'Admin' || user.role === 'HR Manager') && leave.status === 'Pending' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleApprove(leave.id)}
                          className="text-green-600 hover:text-green-900 mr-3"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(leave.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Reject
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Leaves;
